import { useEffect, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ragApi } from '@/api/rag';
import { RagDocument } from '@/types';

export const DocumentsList = ({ sourceId }: { sourceId: number }) => {
  const { t } = useTranslation();
  const observerTarget = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['rag-documents', sourceId],
    queryFn: ({ pageParam = 0 }) => ragApi.getDocuments(sourceId, pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentCount = allPages.flatMap((p) => p.data).length;
      if (currentCount < lastPage.meta.total) {
        return currentCount;
      }
      return undefined;
    },
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage]);

  const documents = data?.pages.flatMap((page) => page.data) || [];

  if (isLoading)
    return <div className="p-4 text-xs text-muted-foreground">{t('common.loading')}</div>;

  if (!documents.length) {
    return (
      <div className="text-sm text-muted-foreground p-2">
        {t('dashboard.chatForm.rag.noDocuments')}
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-2 max-h-60 overflow-y-auto border rounded p-2 bg-slate-50 dark:bg-slate-900">
      {documents.map((doc: RagDocument) => (
        <div key={doc.id} className="text-xs border-b last:border-0 pb-2 mb-2">
          <div className="font-semibold truncate">
            {doc.url ? (
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 dark:text-blue-400 hover:underline"
              >
                {doc.title || doc.url}
              </a>
            ) : (
              doc.title || doc.url
            )}
          </div>
          <div className="text-muted-foreground line-clamp-10 whitespace-pre-wrap">
            {doc.content}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">
            {new Date(doc.createdAt).toLocaleString()}
          </div>
        </div>
      ))}
      <div ref={observerTarget} className="h-4 w-full flex justify-center p-2">
        {isFetchingNextPage && (
          <span className="text-xs text-muted-foreground animate-pulse">{t('common.loading')}...</span>
        )}
      </div>
    </div>
  );
};
